import { useState } from 'react';
import { createUserSchema, USER_ROLE_LABELS, USER_ROLES, type User, type UserRole } from '@faktorplus/shared';
import { useCreateUser, useProjects, useUpdateUser, useUsers } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { PencilIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import pageStyles from '../../components/layout/page.module.css';
import styles from './settings-page.module.css';

export function UsersTab() {
  const { data: users = [], isLoading, error } = useUsers();
  const { data: projects = [] } = useProjects(true);
  const [editing, setEditing] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać użytkowników.</ErrorBox>;

  const projectName = (id: string) => projects.find((project) => project.id === id)?.name ?? '—';

  return (
    <>
      <div className={pageStyles.header}>
        <span className={pageStyles.subtitle}>
          {users.filter((user) => user.isActive).length} aktywnych z {users.length}
        </span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowy użytkownik
          </Button>
        </div>
      </div>

      {users.length === 0 ? (
        <EmptyState mark="∅" title="Brak użytkowników" />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Imię i nazwisko</th>
                <th>E-mail</th>
                <th>Rola</th>
                <th>Budowy</th>
                <th>Stan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={user.isActive ? undefined : styles.inactive}>
                  <td className={pageStyles.strong}>{user.fullName}</td>
                  <td className={pageStyles.docNumber}>{user.email}</td>
                  <td>
                    <span
                      className={`${styles.chip} ${user.role === 'ADMIN' ? styles.chipAccent : ''}`}
                    >
                      {USER_ROLE_LABELS[user.role]}
                    </span>
                  </td>
                  <td className={user.projectIds.length ? undefined : pageStyles.muted}>
                    {user.role === 'SITE_MANAGER'
                      ? user.projectIds.map(projectName).join(', ') || 'brak przypisanych'
                      : 'wszystkie'}
                  </td>
                  <td className={pageStyles.muted}>{user.isActive ? 'Aktywny' : 'Wyłączony'}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button variant="soft" icon={<PencilIcon />} onClick={() => setEditing(user)}>
                        Edytuj
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editing) && (
        <UserDialog
          user={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function UserDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { data: projects = [] } = useProjects();
  const create = useCreateUser();
  const update = useUpdateUser();

  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'SITE_MANAGER');
  const [password, setPassword] = useState('');
  const [projectIds, setProjectIds] = useState<string[]>(user?.projectIds ?? []);
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  function toggleProject(id: string) {
    setProjectIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  async function handleSubmit() {
    setValidationError(null);

    // Without a password the account is created and permanently locked out:
    // there is no self-service reset in the app yet, and no SMTP behind it.
    if (!user && password.length < 10) {
      setValidationError('Podaj hasło startowe (min. 10 znaków) — inaczej użytkownik się nie zaloguje');
      return;
    }

    const payload = {
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      role,
      // Only site managers are scoped to projects; everyone else sees everything.
      projectIds: role === 'SITE_MANAGER' ? projectIds : [],
      ...(password ? { password } : {}),
    };

    const parsed = createUserSchema.safeParse(payload);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (user) {
      const { password: _ignored, ...withoutPassword } = parsed.data;
      await update.mutateAsync({ id: user.id, input: { ...withoutPassword, isActive } });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={user ? 'Edytuj użytkownika' : 'Nowy użytkownik'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {user ? 'Zapisz zmiany' : 'Dodaj użytkownika'}
          </Button>
        </>
      }
    >
      <div className={modalStyles.row}>
        <Field label="Imię i nazwisko">
          {(id) => (
            <Input
              id={id}
              value={fullName}
              autoFocus={!user}
              placeholder="Bartek Kowalski"
              onChange={(event) => setFullName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Rola">
          {(id) => (
            <Select
              id={id}
              value={role}
              onChange={(event) => setRole(event.target.value as UserRole)}
            >
              {USER_ROLES.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {USER_ROLE_LABELS[candidate]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field label="Adres e-mail">
        {(id) => (
          <Input
            id={id}
            type="email"
            value={email}
            placeholder="bartek@faktorplus.pl"
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </Field>

      {!user && (
        <Field
          label="Hasło startowe"
          hint="Przekaż je użytkownikowi — samodzielny reset hasła wymaga skonfigurowanego SMTP, którego jeszcze nie ma."
        >
          {(id) => (
            <Input
              id={id}
              type="text"
              value={password}
              placeholder="min. 10 znaków"
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
      )}

      {role === 'SITE_MANAGER' && (
        <Field label="Budowy, które prowadzi">
          {() =>
            projects.length === 0 ? (
              <p className={pageStyles.muted}>Najpierw dodaj budowę.</p>
            ) : (
              <div className={modalStyles.checkList}>
                {projects.map((project) => (
                  <label key={project.id} className={modalStyles.checkItem}>
                    <input
                      type="checkbox"
                      className={pageStyles.checkbox}
                      checked={projectIds.includes(project.id)}
                      onChange={() => toggleProject(project.id)}
                    />
                    {project.name}
                  </label>
                ))}
              </div>
            )
          }
        </Field>
      )}

      {user && (
        <label className={modalStyles.checkItem}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Konto aktywne
        </label>
      )}

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
